import { useQuery } from "@tanstack/react-query";
import { ButtonSecondary } from "components/Atoms/Button";
import { Loading } from "components/panels";
import moment from "moment";
import { PlanningContext } from "PlanningContext/PlanningContext";
import React, { useContext } from "react";
import Panel from "../../components/panels/panel";
import AppointmentTypeFilter from "./AppointmentTypeFilter";
import { fireLinkedTravelUpdated, type LinkType, onLinkToggled } from "./DOMEvents";
import Itinerary from "./Itinerary";
import PhaseFilter from "./PhaseFilter";
import { ItinerarySidebarItem, LinkedItineraryItems } from "./utils";

export interface ItinerarySidebarBaseProps {
  item: ItinerarySidebarItem
  artist: server.dto.ArtistSmall
  startDate: moment.Moment
  endDate: moment.Moment

  disableNewAppointments: boolean
  canLinkUnlinkTravel: boolean
  canLinkUnlinkShow: boolean
  canAdd: boolean

  queryKeys: string

  linkedItems: LinkedItineraryItems

  onLinkUnlink: () => void
}

const itineraryTypesKey = "itinerary_types";
const itineraryPhasesKey = "itinerary_phases";

const getFromLocalStorage = (key: string): string[] => {
  const values = localStorage.getItem(key);
  if (values) {
    return values.split(",");
  }
  return undefined;
};

const setLocalStorage = (key: string, values: string[]) => {
  localStorage.setItem(key, values.join(","));
};

const defaultAppointmentTypes = serverReference.appointmentTypes.map(a => a.value);
const defaultPhases = serverReference.appointmentPhases.map(a => a.value);

export const itinerarySidebarQueryKey = "itinerarySidebar";

const ItinerarySidebarBase = (props: ItinerarySidebarBaseProps) => {
  const planningContext = useContext(PlanningContext);

  const [startDate, setStartDate] = React.useState(moment.utc(props.startDate).subtract(2, "days"));
  const [endDate, setEndDate] = React.useState(moment.utc(props.endDate).add(3, "days"));
  const [phases, setPhases] = React.useState<server.dto.AppointmentPhases[]>(getFromLocalStorage(itineraryPhasesKey) as server.dto.AppointmentPhases[] ?? defaultPhases);
  const [appointmentTypes, setAppointmentTypes] = React.useState<server.dto.AppointmentTypes[]>(getFromLocalStorage(itineraryTypesKey) as server.dto.AppointmentTypes[] ?? defaultAppointmentTypes);

  const [linkedFlightIds, setLinkedFlightIds] = React.useState<number[]>(props.linkedItems.flightIds);
  const [linkedGroundTransportIds, setLinkedGroundTransportIds] = React.useState<number[]>(props.linkedItems.groundTransportIds);
  const [linkedTrainSegmentIds, setLinkedTrainSegmentIds] = React.useState<number[]>(props.linkedItems.trainSegmentIds);
  const [linkedShowIds, setLinkedShowIds] = React.useState<number[]>(props.linkedItems.showIds);

  const instruction: server.dto.PlanningForItineraryQuery = {
    start: startDate.toISOString() as any,
    end: endDate.toISOString() as any,
    phases,
    appointmentTypes: appointmentTypes,
    artistIds: [props.artist.artistId],
  };

  const daysBetweenStartEnd = endDate.diff(startDate, "days");

  const api = useQuery({
    queryKey: [itinerarySidebarQueryKey, props.queryKeys, instruction],
    queryFn: async () => {
      return window.CoreApi.Planning.PlanningForItinerary(instruction);
    },
    keepPreviousData: true,
    enabled: daysBetweenStartEnd < serverReference.constants.appointment.maxDurationDays
  });
  const items = api.data?.data.data ?? [];

  const loadPreviousAppointments = () => {
    const newStart = startDate.clone().subtract(2, "days");
    setStartDate(newStart);
  };

  const loadNextAppointments = () => {
    const newEnd = endDate.clone().add(3, "days");
    setEndDate(newEnd);
  };

  const onAppointmentToggled = (type: server.dto.AppointmentTypes) => {
    const doesTypeAlreadyExist = appointmentTypes.includes(type);
    let newTypes: server.dto.AppointmentTypes[];
    if (doesTypeAlreadyExist) {
      newTypes = appointmentTypes.filter(t => t !== type);
    } else {
      newTypes = [...appointmentTypes, type];
    }

    if (newTypes.length === 0) {
      newTypes = serverReference.appointmentTypes.map(a => a.value).filter(a => a !== "unknown");
    }
    setAppointmentTypes(newTypes);
    setLocalStorage(itineraryTypesKey, newTypes);
  };

  const onPhaseToggled = (phase: server.dto.AppointmentPhases) => {
    const doesPhaseAlreadyExist = phases.includes(phase);
    let newPhases: server.dto.AppointmentPhases[];
    if (doesPhaseAlreadyExist) {
      newPhases = phases.filter(p => p !== phase);
    } else {
      newPhases = [...phases, phase];
    }
    if (newPhases.length === 0) {
      newPhases = serverReference.appointmentPhases.map(a => a.value).filter(a => a !== "unknown");
    }
    setPhases(newPhases);
    setLocalStorage(itineraryPhasesKey, newPhases);
  };

  const refetchShowLinkedItems = async () => {
    if (props.item.type !== "show") return;

    const linkedTravelRes = await window.CoreApi.Show.GetLinkedTravel(props.item.itemId);
    fireLinkedTravelUpdated(linkedTravelRes.data.data);

    setLinkedFlightIds(linkedTravelRes.data.data.flights.map(s => s.flightId));
    setLinkedGroundTransportIds(linkedTravelRes.data.data.groundTransport.map(s => s.groundTransportId));
    setLinkedTrainSegmentIds(linkedTravelRes.data.data.trains.map(s => s.trainSegmentId));
  }

  const toggleLinkedFlight = async (appointment: server.dto.PlannerAppointment) => {
    if (props.item.type !== "show") return;

    const isAlreadyLinked = linkedFlightIds.includes(appointment.flightId);

    if (isAlreadyLinked) {
      setLinkedFlightIds(oldIds => oldIds.filter(oldId => oldId !== appointment.flightId));
      await window.CoreApi.Show.UnlinkFlight({
        flightId: appointment.flightId,
        showId: props.item.itemId,
      });
    } else {
      setLinkedFlightIds(oldIds => [...oldIds, appointment.flightId]);
      await window.CoreApi.Show.LinkFlight({
        flightId: appointment.flightId,
        showId: props.item.itemId,
      });
    }

    await refetchShowLinkedItems();
  }

  const toggleLinkedTrainSegment = async (appointment: server.dto.PlannerAppointment) => {
    if (props.item.type !== "show") return;

    const isAlreadyLinked = linkedTrainSegmentIds.includes(appointment.trainSegmentId);

    if (isAlreadyLinked) {
      setLinkedTrainSegmentIds(oldIds => oldIds.filter(oldId => oldId !== appointment.trainSegmentId));
      await window.CoreApi.Show.UnlinkTrainSegment({
        trainSegmentId: appointment.trainSegmentId,
        showId: props.item.itemId,
      });
    } else {
      setLinkedTrainSegmentIds(oldIds => [...oldIds, appointment.trainSegmentId]);
      await window.CoreApi.Show.LinkTrainSegment({
        trainSegmentId: appointment.trainSegmentId,
        showId: props.item.itemId,
      });
    }

    await refetchShowLinkedItems();
  }

  const toggleLinkedGroundTransport = async (appointment: server.dto.PlannerAppointment) => {
    if (props.item.type !== "show") return;

    const isAlreadyLinked = linkedGroundTransportIds.includes(appointment.groundTransportId);

    if (isAlreadyLinked) {
      setLinkedGroundTransportIds(oldIds => oldIds.filter(oldId => oldId !== appointment.groundTransportId));
      await window.CoreApi.Show.UnlinkGroundTransport({
        groundTransportId: appointment.groundTransportId,
        showId: props.item.itemId,
      });
    } else {
      setLinkedGroundTransportIds(oldIds => [...oldIds, appointment.groundTransportId]);
      await window.CoreApi.Show.LinkGroundTransport({
        groundTransportId: appointment.groundTransportId,
        showId: props.item.itemId,
      });
    }

    await refetchShowLinkedItems();
  }

  const toggleLinkedShow = async (appointment: server.dto.PlannerAppointment) => {
    if (props.item.type === "show") return;

    const isAlreadyLinked = linkedShowIds.includes(appointment.showId);

    if (isAlreadyLinked) {
      setLinkedShowIds(oldIds => oldIds.filter(oldId => oldId !== appointment.showId));
      if (props.item.type === "groundTransport") {
        await window.CoreApi.Show.UnlinkGroundTransport({
          groundTransportId: props.item.itemId,
          showId: appointment.showId,
        });
      } else if (props.item.type === "flight") {
        await window.CoreApi.Show.UnlinkFlight({
          flightId: props.item.itemId,
          showId: appointment.showId,
        });
      }
    } else {
      setLinkedShowIds(oldIds => [...oldIds, appointment.showId]);
      if (props.item.type === "groundTransport") {
        await window.CoreApi.Show.LinkGroundTransport({
          groundTransportId: props.item.itemId,
          showId: appointment.showId,
        });
      } else if (props.item.type === "flight") {
        await window.CoreApi.Show.LinkFlight({
          flightId: props.item.itemId,
          showId: appointment.showId,
        });
      }
    }

    props.onLinkUnlink();
  }

  const toggleLinked = async (appointment: server.dto.PlannerAppointment) => {
    switch (appointment.type.value) {
      case "flight":
        await toggleLinkedFlight(appointment);
        return;
      case "train":
        await toggleLinkedTrainSegment(appointment);
        return;
      case "groundTransport":
        await toggleLinkedGroundTransport(appointment);
        return;
      case "show":
        await toggleLinkedShow(appointment);
        return;
    }
  };

  const onLinkToggle = (linkType: LinkType, id: number) => {
    let setter: React.Dispatch<React.SetStateAction<number[]>>;
    switch (linkType) {
      case "flight": setter = setLinkedFlightIds; break;
      case "groundTransport": setter = setLinkedGroundTransportIds; break;
      case "train": setter = setLinkedTrainSegmentIds; break;
    }
    setter(oldValues => {
      if (oldValues.includes(id)) {
        return oldValues.filter(oldId => oldId !== id);
      } else {
        return [...oldValues, id];
      }
    });
  };

  React.useEffect(() => {
    const removeLinkToggleEventHandler = onLinkToggled(onLinkToggle);
    return removeLinkToggleEventHandler;
  }, []);

  React.useEffect(() => {
    setStartDate(moment.utc(props.startDate).subtract(2, "days"));
    setEndDate(moment.utc(props.endDate).add(3, "days"));
  }, [props.endDate, props.startDate]);

  const typeFilterMap = new Set(appointmentTypes);
  const phaseFilterMap = new Set(phases);

  const onNewButtonPressed = () => {
    planningContext.open({
      artist: props.artist,
      date: startDate,
      fromShowId: props.item.type === "show" ? props.item.itemId : undefined,
    });
  };

  const canAddNewAppointments = props.canAdd && !props.disableNewAppointments;

  return <Panel
    panelClassName="s1_related-feature-panel"
    title={props.artist.name}
    contentCssClass="flex-column gap-larger"
    button={canAddNewAppointments && <ButtonSecondary onClick={onNewButtonPressed} label={RESX.Buttons.btnNew} />}
  >

    <div style={{ display: "table", width: "100%" }} className="margin-top-larger">
      <div style={{ display: "table-cell" }}>
        <AppointmentTypeFilter type="show" isToggled={typeFilterMap.has("show")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="flight" isToggled={typeFilterMap.has("flight")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="train" isToggled={typeFilterMap.has("train")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="groundTransport" isToggled={typeFilterMap.has("groundTransport")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="accommodation" isToggled={typeFilterMap.has("accommodation")} onToggled={onAppointmentToggled} />
        <AppointmentTypeFilter type="externalCalendar" isToggled={typeFilterMap.has("externalCalendar")} onToggled={onAppointmentToggled} />
      </div>
      <div style={{ display: "table-cell", textAlign: "right" }}>
        <PhaseFilter phase={"option"} isToggled={phaseFilterMap.has("option")} onToggled={onPhaseToggled} />
        <PhaseFilter phase={"confirmed"} isToggled={phaseFilterMap.has("confirmed")} onToggled={onPhaseToggled} />
        <PhaseFilter phase={"cancelled"} isToggled={phaseFilterMap.has("cancelled")} onToggled={onPhaseToggled} />
      </div>
    </div>
    <div>
      <div className="s1_morebutton s1_previous" onClick={loadPreviousAppointments}>
        <i className="icon-angle-up"></i>
      </div>
      {items.length > 0 && <Itinerary
        artist={props.artist}
        appointments={items}
        startMoment={startDate}
        endMoment={endDate}
        item={props.item}
        linkedItems={{
          flightIds: linkedFlightIds,
          groundTransportIds: linkedGroundTransportIds,
          trainSegmentIds: linkedTrainSegmentIds,
          showIds: linkedShowIds,
        }}
        toggleLinked={toggleLinked}
        canCreateNewAppointment={canAddNewAppointments}
        canLinkUnlinkTravel={props.canLinkUnlinkTravel}
        canLinkUnlinkShow={props.canLinkUnlinkShow}
      />}
      {api.isInitialLoading && <Loading height={50} />}
      <div className="s1_morebutton s1_next" onClick={loadNextAppointments}>
        <i className="icon-angle-down"></i>
      </div>
    </div>
  </Panel>;
};

export default ItinerarySidebarBase;